import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Copy, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Shown when a tenant user attempts to sign in via the generic /login page
 * (or a URL whose slug/role don't match their account). Displays their
 * customized portal URL with a Copy button + an "Open" shortcut.
 */
export default function CustomUrlDialog({ open, customUrl, userEmail, userRole, onClose }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!customUrl) return;
    try {
      await navigator.clipboard.writeText(customUrl);
      setCopied(true);
      toast.success("URL copied — paste it in your browser to sign in.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please long-press to copy manually.");
    }
  };

  const openInNewTab = () => {
    if (customUrl) window.location.assign(customUrl);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="wrong-url-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="w-5 h-5" /> Wrong sign-in URL
          </DialogTitle>
          <DialogDescription className="pt-1">
            Your <span className="font-mono font-semibold text-foreground">{userRole || "account"}</span> is provisioned under a <strong>customized portal URL</strong>. General <span className="font-mono">/login</span> is for platform administrators only. Please continue on your dedicated URL below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {userEmail && (
            <div className="text-[11px] text-muted-foreground">Account: <span className="font-mono text-foreground">{userEmail}</span></div>
          )}
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Your portal URL</div>
            <div className="text-xs sm:text-sm font-mono break-all text-foreground" data-testid="wrong-url-target">{customUrl}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={copy} variant="outline" className="flex-1" data-testid="wrong-url-copy">
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy URL</>}
            </Button>
            <Button onClick={openInNewTab} className="flex-1" data-testid="wrong-url-open">
              <ExternalLink className="w-4 h-4 mr-1" /> Open my portal
            </Button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
            💡 Bookmark this URL — even if you sign out or close the browser, use this exact link every time. Sharing it, entering credentials on the general login page, or bookmarking the wrong page will surface this warning again.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="wrong-url-close">Try a different account</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
